import React, { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, ArrowRightLeft, X, Check } from "lucide-react";
import { listMigrations, updateMigration, getOnboardingFunnel } from "../services/api";
import type { DiscoveryRecord, MigrationStatus } from "../types";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

/* ----------------------------------------------------------------------------
 * Onboarding discovery + migration requests.
 *
 * Self-service signups fill in a short discovery step when they create their
 * workspace (current CRM, do they want a migration, what they dislike). This
 * board is where sales + product read those answers and triage the migration
 * requests. Nothing migrates automatically; approving here just flags intent
 * (the migration itself is run with the existing tooling).
 * --------------------------------------------------------------------------*/

const STATUS_FILTERS: { value: "" | MigrationStatus; label: string }[] = [
  { value: "", label: "All submissions" },
  { value: "requested", label: "Migration requested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "done", label: "Done" },
  { value: "none", label: "No migration" }
];

const STATUS_META: Record<MigrationStatus, { label: string; color: string; bg: string }> = {
  none: { label: "No migration", color: "var(--text-mute)", bg: "transparent" },
  requested: { label: "Requested", color: "#b06a00", bg: "rgba(191,124,0,0.12)" },
  approved: { label: "Approved", color: "var(--success)", bg: "rgba(46,160,67,0.12)" },
  rejected: { label: "Rejected", color: "var(--danger)", bg: "rgba(207,60,60,0.12)" },
  done: { label: "Done", color: "var(--text-mute)", bg: "rgba(127,127,127,0.12)" }
};

// The approve->setup runbook, reusing the Rocksoft migration pattern. Operators
// work an approved migration through these steps; completion is stored per step.
const RUNBOOK_STEPS: { key: string; label: string; hint: string }[] = [
  { key: "access", label: "Get access or an export", hint: "Ask the customer to export their data or grant access to their current CRM." },
  { key: "mapping", label: "Map their fields to Orbit", hint: "Match customers, contacts and owners to Orbit's model." },
  { key: "dryrun", label: "Dry-run the import", hint: "Import into a staging tenant first, idempotent like the Rocksoft migration." },
  { key: "verify", label: "Verify the data", hint: "Check record counts and spot-check a few records against the source." },
  { key: "golive", label: "Import to their live workspace", hint: "Run the final import into the customer's real tenant." }
];

// Ordered onboarding funnel + engagement events for the funnel card (Phase 6).
const FUNNEL_STEPS: { key: string; label: string }[] = [
  { key: "signup_created", label: "Signed up" },
  { key: "explore_entered", label: "Explored demo" },
  { key: "pay_started", label: "Started payment" },
  { key: "activated", label: "Activated" }
];
const ENGAGEMENT_STEPS: { key: string; label: string }[] = [
  { key: "tour_completed", label: "Finished tour" },
  { key: "checklist_completed", label: "Finished checklist" }
];

function Badge({ status }: { status: MigrationStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.none;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: m.color,
        background: m.bg,
        border: `1px solid ${m.color === "var(--text-mute)" ? "rgba(127,127,127,0.35)" : m.color}`,
        whiteSpace: "nowrap"
      }}
    >
      {m.label}
    </span>
  );
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export function MigrationsPage() {
  usePageTitle("Migrations");
  const toast = useToast();

  const [items, setItems] = useState<DiscoveryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"" | MigrationStatus>("");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DiscoveryRecord | null>(null);
  const [funnel, setFunnel] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    getOnboardingFunnel()
      .then((r) => {
        const m: Record<string, number> = {};
        (r.events || []).forEach((e) => { m[e.event] = e.tenants; });
        setFunnel(m);
      })
      .catch(() => setFunnel({}));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMigrations({
        status: status || undefined,
        q: appliedQ.trim() || undefined
      });
      setItems(res.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [status, appliedQ]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const t = window.setTimeout(() => setAppliedQ(q), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  // Apply any migration patch (status / note / runbook), folding the fresh row
  // back into the list and the open detail. Returns the updated record or null.
  const applyPatch = useCallback(
    async (
      businessId: string,
      patchInput: { migrationStatus?: MigrationStatus; note?: string | null; runbook?: Record<string, boolean> }
    ): Promise<DiscoveryRecord | null> => {
      setSavingId(businessId);
      try {
        const res = await updateMigration(businessId, patchInput);
        setItems((prev) => prev.map((it) => (it.businessId === businessId ? res.discovery : it)));
        setDetail((d) => (d && d.businessId === businessId ? res.discovery : d));
        return res.discovery;
      } catch (err) {
        toast.push("error", err instanceof Error ? err.message : "Could not update the migration.");
        return null;
      } finally {
        setSavingId(null);
      }
    },
    [toast]
  );

  const setMigration = useCallback(
    async (businessId: string, next: MigrationStatus) => {
      const r = await applyPatch(businessId, { migrationStatus: next });
      if (r) toast.push("success", `Migration marked ${STATUS_META[next].label.toLowerCase()}.`);
    },
    [applyPatch, toast]
  );

  const requestedCount = items.filter((i) => i.migrationStatus === "requested").length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Onboarding</span>
          <h1>Migrations</h1>
          <p className="muted">
            What new signups told us in the create-workspace discovery step.
            {requestedCount > 0 ? ` ${requestedCount} awaiting review.` : ""}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void load()} title="Refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {funnel ? <FunnelCard funnel={funnel} /> : null}

      {error ? (
        <div className="card" style={{ padding: 12, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div>
      ) : null}

      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search company, CRM, gripes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value as "" | MigrationStatus)} style={{ maxWidth: 220 }}>
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Current CRM</th>
              <th>Wants migration</th>
              <th>Package</th>
              <th>What they dislike</th>
              <th>Status</th>
              <th>Signed up</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state"><p className="muted">Loading submissions…</p></div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <ArrowRightLeft size={20} />
                    <h3>No discovery submissions yet</h3>
                    <p>They appear here as new signups create their workspace.</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.businessId}>
                  <td>
                    <button
                      type="button"
                      onClick={() => setDetail(it)}
                      style={{
                        fontWeight: 600,
                        textAlign: "left",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: "var(--accent, #7c5cff)"
                      }}
                    >
                      {it.companyName || "(unnamed)"}
                    </button>
                    <div className="muted" style={{ fontSize: 11.5 }}>{it.contactEmail}</div>
                  </td>
                  <td>{it.currentCrm || <span className="muted">-</span>}</td>
                  <td>{it.wantsMigration ? <Badge status="requested" /> : <span className="muted">No</span>}</td>
                  <td className="muted nowrap">
                    {it.pack || "-"}{it.seats ? ` · ${it.seats} ${it.seats === 1 ? "seat" : "seats"}` : ""}
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    {it.dislikes ? (
                      <span title={it.dislikes} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {it.dislikes}
                      </span>
                    ) : <span className="muted">-</span>}
                  </td>
                  <td><Badge status={it.migrationStatus} /></td>
                  <td className="muted nowrap">{fmtDate(it.createdAt)}</td>
                  <td className="right nowrap">
                    <MigrationActions
                      record={it}
                      busy={savingId === it.businessId}
                      onSet={(next) => void setMigration(it.businessId, next)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detail ? (
        <MigrationDetail
          record={detail}
          busy={savingId === detail.businessId}
          onClose={() => setDetail(null)}
          onSetStatus={(next) => void setMigration(detail.businessId, next)}
          onToggleStep={(key, done) => void applyPatch(detail.businessId, { runbook: { [key]: done } })}
          onSaveNote={(note) => applyPatch(detail.businessId, { note })}
        />
      ) : null}
    </div>
  );
}

function MigrationDetail({
  record,
  busy,
  onClose,
  onSetStatus,
  onToggleStep,
  onSaveNote
}: {
  record: DiscoveryRecord;
  busy: boolean;
  onClose: () => void;
  onSetStatus: (next: MigrationStatus) => void;
  onToggleStep: (key: string, done: boolean) => void;
  onSaveNote: (note: string) => Promise<DiscoveryRecord | null>;
}) {
  const [note, setNote] = React.useState(record.migrationNote ?? "");
  const [savingNote, setSavingNote] = React.useState(false);
  React.useEffect(() => {
    setNote(record.migrationNote ?? "");
  }, [record.businessId, record.migrationNote]);

  const runbook = record.migrationRunbook ?? {};
  const doneSteps = RUNBOOK_STEPS.filter((s) => runbook[s.key]).length;
  const s = record.migrationStatus;
  const showRunbook = s === "approved" || s === "done" || doneSteps > 0;

  const saveNote = async () => {
    setSavingNote(true);
    await onSaveNote(note.trim());
    setSavingNote(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Migration detail"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        justifyContent: "flex-end"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          height: "100%",
          overflowY: "auto",
          background: "var(--surface, #fff)",
          color: "var(--text, #0f172a)",
          boxShadow: "-16px 0 40px rgba(0,0,0,0.25)",
          padding: 22
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>{record.companyName || "(unnamed)"}</h2>
            <div className="muted" style={{ fontSize: 12.5 }}>{record.contactEmail}</div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="btn btn-ghost btn-sm">
            <X size={16} />
          </button>
        </div>

        <div style={{ margin: "16px 0", display: "grid", gap: 8 }}>
          <DetailRow label="Status"><Badge status={record.migrationStatus} /></DetailRow>
          <DetailRow label="Current CRM">{record.currentCrm || <span className="muted">Not given</span>}</DetailRow>
          <DetailRow label="Wants migration">{record.wantsMigration ? "Yes" : "No"}</DetailRow>
          <DetailRow label="Package">
            {record.pack || "-"}{record.seats ? ` · ${record.seats} ${record.seats === 1 ? "seat" : "seats"}` : ""}
          </DetailRow>
          <DetailRow label="Signed up">{fmtDate(record.createdAt)}</DetailRow>
        </div>

        {record.dislikes ? (
          <div style={{ marginBottom: 16 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>What they dislike about their CRM</div>
            <div style={{ background: "var(--surface-2, rgba(127,127,127,0.08))", borderRadius: 8, padding: "8px 10px", fontSize: 13 }}>
              {record.dislikes}
            </div>
          </div>
        ) : null}

        {/* Status actions */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          {(s === "requested" || s === "rejected") && (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onSetStatus("approved")}>
              Approve migration
            </button>
          )}
          {(s === "requested" || s === "approved") && (
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSetStatus("rejected")}>
              Reject
            </button>
          )}
          {s === "approved" && (
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSetStatus("done")}>
              Mark done
            </button>
          )}
        </div>

        {/* Runbook */}
        {showRunbook ? (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <strong style={{ fontSize: 14 }}>Setup runbook</strong>
              <span className="muted" style={{ fontSize: 12 }}>{doneSteps}/{RUNBOOK_STEPS.length} done</span>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {RUNBOOK_STEPS.map((step) => {
                const done = !!runbook[step.key];
                return (
                  <li key={step.key} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <button
                      type="button"
                      aria-pressed={done}
                      aria-label={done ? `Mark "${step.label}" not done` : `Mark "${step.label}" done`}
                      disabled={busy}
                      onClick={() => onToggleStep(step.key, !done)}
                      style={{
                        flex: "0 0 auto",
                        marginTop: 1,
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: done ? "var(--success, #2ea043)" : "transparent",
                        border: done ? "none" : "1.5px solid rgba(127,127,127,0.5)",
                        color: "#fff"
                      }}
                    >
                      {done ? <Check size={13} /> : null}
                    </button>
                    <span>
                      <span style={{ fontWeight: 500, textDecoration: done ? "line-through" : "none" }}>{step.label}</span>
                      <span className="muted" style={{ display: "block", fontSize: 12 }}>{step.hint}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="muted" style={{ fontSize: 12.5, marginBottom: 18 }}>
            Approve the migration to start the setup runbook.
          </p>
        )}

        {/* Operator notes */}
        <div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Operator notes</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Coordination notes, contact, source-CRM specifics…"
            style={{ width: "100%" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy || savingNote || note.trim() === (record.migrationNote ?? "")}
              onClick={() => void saveNote()}
            >
              {savingNote ? "Saving…" : "Save note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
      <span className="muted">{label}</span>
      <span style={{ textAlign: "right", fontWeight: 500 }}>{children}</span>
    </div>
  );
}

function FunnelCard({ funnel }: { funnel: Record<string, number> }) {
  const top = funnel[FUNNEL_STEPS[0].key] ?? 0;
  const max = Math.max(1, ...FUNNEL_STEPS.map((s) => funnel[s.key] ?? 0));
  const anyData =
    FUNNEL_STEPS.some((s) => (funnel[s.key] ?? 0) > 0) ||
    ENGAGEMENT_STEPS.some((s) => (funnel[s.key] ?? 0) > 0);

  if (!anyData) {
    return (
      <div className="card" style={{ padding: 16 }}>
        <strong>Onboarding funnel</strong>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
          No funnel data yet. Events appear here as new signups explore, pay and activate.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <strong>Onboarding funnel</strong>
        <span className="muted" style={{ fontSize: 12 }}>last 90 days · distinct tenants</span>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {FUNNEL_STEPS.map((s) => {
          const n = funnel[s.key] ?? 0;
          const pctOfTop = top > 0 ? Math.round((n / top) * 100) : 0;
          const width = Math.round((n / max) * 100);
          return (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 130, fontSize: 13 }}>{s.label}</div>
              <div style={{ flex: 1, background: "rgba(127,127,127,0.12)", borderRadius: 6, height: 22, overflow: "hidden" }}>
                <div style={{ width: `${width}%`, height: "100%", background: "var(--accent, #7c5cff)", opacity: 0.85, borderRadius: 6 }} />
              </div>
              <div style={{ width: 96, textAlign: "right", fontSize: 13 }}>
                <strong>{n}</strong>
                {s.key !== FUNNEL_STEPS[0].key ? <span className="muted"> · {pctOfTop}%</span> : null}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
        {ENGAGEMENT_STEPS.map((s) => (
          <span key={s.key} className="muted" style={{ fontSize: 12.5 }}>
            {s.label}: <strong style={{ color: "var(--text)" }}>{funnel[s.key] ?? 0}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function MigrationActions({
  record,
  busy,
  onSet
}: {
  record: DiscoveryRecord;
  busy: boolean;
  onSet: (next: MigrationStatus) => void;
}) {
  // Only rows where the customer asked to migrate get a lifecycle. Pure
  // discovery rows (no migration wanted) have nothing to action.
  if (!record.wantsMigration && record.migrationStatus === "none") {
    return <span className="muted">-</span>;
  }
  const s = record.migrationStatus;
  return (
    <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
      {(s === "requested" || s === "rejected") && (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSet("approved")}>
          Approve
        </button>
      )}
      {(s === "requested" || s === "approved") && (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSet("rejected")}>
          Reject
        </button>
      )}
      {s === "approved" && (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSet("done")}>
          Mark done
        </button>
      )}
    </div>
  );
}
